import type { ReactElement } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../../lib/utils.js";
import { TableScroll } from "../../molecules/TableScroll/index.js";

type MarkdownContentVariant = "compact" | "article";

/**
 * `fit` is today's table: squeezed to the container. `scroll` keeps a wide
 * table inside its own horizontal scroll region with readable, top-aligned
 * columns, so it can never widen what holds it (the Player AI chat, Task 8).
 */
type MarkdownTableLayout = "fit" | "scroll";

interface MarkdownContentProps {
  readonly markdown: string;
  readonly className?: string;
  /** Compact is the existing widget/rail treatment; article is for long-form reading. */
  readonly variant?: MarkdownContentVariant;
  readonly tableLayout?: MarkdownTableLayout;
}

/* A cell's content box is bounded both ways: never squeezed below a readable
   column, never wider than a comfortable line, so a long cell wraps instead of
   stretching the table. A block inside the cell is used because min/max width
   on a table cell itself is undefined in CSS 2.1 and varies by engine. */
const SCROLL_TABLE_CELL = "min-w-32 max-w-72";

const SCROLL_TABLE_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <TableScroll
      aria-label="Table"
      pinFirstColumn={false}
      embedded
      className="my-3"
    >
      {/* The variant's `[&_table]` margin belongs to the region now, so the
          table itself sits flush inside it (an inline style outranks the
          descendant rule). */}
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a table authored in markdown by Player AI; DataTable needs typed columns and rows this renderer does not have. */}
      <table {...props} style={{ ...props.style, marginBlock: 0 }} />
    </TableScroll>
  ),
  th: ({ node: _node, children, className, ...props }) => (
    <th {...props} className={cn("align-top", className)}>
      <div className={SCROLL_TABLE_CELL}>{children}</div>
    </th>
  ),
  td: ({ node: _node, children, className, ...props }) => (
    <td {...props} className={cn("align-top", className)}>
      <div className={SCROLL_TABLE_CELL}>{children}</div>
    </td>
  ),
};

const VARIANT_CLASSES: Record<MarkdownContentVariant, readonly string[]> = {
  compact: [
    "text-sm leading-6",
    "[&_blockquote]:my-3 [&_blockquote]:border-s-2 [&_blockquote]:border-pb-border [&_blockquote]:ps-3 [&_blockquote]:text-pb-text-muted",
    "[&_h1]:mb-3 [&_h1]:mt-4 [&_h1]:text-xl [&_h1]:font-semibold",
    "[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold",
    "[&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-semibold",
    "[&_hr]:my-4 [&_hr]:border-pb-border",
    "[&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:ps-5",
    "[&_p]:my-2 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-pb-card-elevated [&_pre]:p-3",
    "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-start",
    "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:ps-5",
  ],
  article: [
    "font-sans text-base leading-7",
    "[&_a]:decoration-pb-border-strong [&_a:hover]:decoration-pb-teal-text",
    "[&_blockquote]:my-6 [&_blockquote]:border-s-2 [&_blockquote]:border-pb-amber [&_blockquote]:ps-4 [&_blockquote]:text-lg [&_blockquote]:italic [&_blockquote]:leading-7 [&_blockquote]:text-pb-text-secondary",
    "[&_h1]:mb-4 [&_h1]:mt-10 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:leading-8 [&_h1]:tracking-tight",
    "[&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:leading-8 [&_h2]:tracking-tight",
    "[&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:leading-7",
    "[&_hr]:my-8 [&_hr]:border-pb-border",
    "[&_li]:my-1.5 [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:ps-6",
    "[&_p]:my-4 [&_pre]:my-5 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-pb-card-elevated [&_pre]:p-4",
    "[&_strong]:font-semibold",
    "[&_table]:my-6 [&_table]:w-full [&_table]:border-collapse [&_table]:font-sans [&_table]:text-sm [&_table]:leading-6",
    "[&_ul]:my-5 [&_ul]:list-disc [&_ul]:ps-6",
  ],
};

/** Render bounded Markdown prose; skip raw HTML/images and leave sanitized empty links as plain text. */
export function MarkdownContent({
  markdown,
  className,
  variant = "compact",
  tableLayout = "fit",
}: MarkdownContentProps): ReactElement {
  return (
    <div
      className={cn(
        "min-w-0 text-pb-text",
        "[&_a]:font-medium [&_a]:text-pb-teal-text [&_a]:underline [&_a]:underline-offset-2",
        "[&_code]:rounded [&_code]:bg-pb-card-elevated [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs",
        "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
        "[&_td]:border [&_td]:border-pb-border [&_td]:p-2",
        "[&_th]:border [&_th]:border-pb-border [&_th]:bg-pb-card-elevated [&_th]:p-2 [&_th]:font-semibold",
        ...VARIANT_CLASSES[variant],
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          ...(tableLayout === "scroll" ? SCROLL_TABLE_COMPONENTS : {}),
          a: ({ node: _node, children, ...props }) =>
            props.href ? (
              // eslint-disable-next-line ds/no-hand-built -- kept: a markdown link styled by the prose container's own link rules; Link brings its own ink and focus treatment, and no approved look row lists the change.
              <a {...props} target="_blank" rel="noreferrer noopener">
                {children}
              </a>
            ) : (
              <span>{children}</span>
            ),
          img: () => null,
          input: ({ node: _node, ...props }) => (
            // eslint-disable-next-line ds/no-hand-built -- kept: a read-only markdown task-list checkbox rendered inside prose.
            <input
              {...props}
              aria-label={
                props.checked === true ? "Completed task" : "Incomplete task"
              }
            />
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
